import React, { useRef, useState, useMemo } from 'react';
import '../styles/MarkdownInput.css';
import { renderHTML } from '../functions/rendering';

function MarkdownInput(props) {
    const label = props.label;
    const labelType = props.labelType;
    const value = props.value;
    const onChange = props.onChange;
    const placeholder = props.placeholder;
    const disabled = props.disabled;
    const onBlur = props.onBlur;
    const rows = props.rows ?? 5;

    const [previewing, setPreviewing] = useState(false);
    const textareaRef = useRef(null);

    const labelStyle = labelType === "default"
        ? "body-bold colour-primary"
        : "label-bold colour-primary";

    const previewHtml = useMemo(() => {
        if (!previewing || !value) return '';
        return renderHTML(value);
    }, [previewing, value]);

    const insertMarkdown = (before, after) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = value.substring(start, end);
        const newValue = value.substring(0, start) + before + selected + after + value.substring(end);

        // Create a synthetic-like event for the onChange handler
        const syntheticEvent = { target: { value: newValue } };
        onChange(syntheticEvent);

        // Restore cursor position after React re-renders
        requestAnimationFrame(() => {
            if (selected.length > 0) {
                textarea.setSelectionRange(start + before.length, end + before.length);
            } else {
                const cursorPos = start + before.length;
                textarea.setSelectionRange(cursorPos, cursorPos);
            }
            textarea.focus();
        });
    };

    const insertLinePrefix = (prefix) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        // Find the beginning of the current line
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const newValue = value.substring(0, lineStart) + prefix + value.substring(lineStart);

        const syntheticEvent = { target: { value: newValue } };
        onChange(syntheticEvent);

        requestAnimationFrame(() => {
            const cursorPos = start + prefix.length;
            textarea.setSelectionRange(cursorPos, cursorPos);
            textarea.focus();
        });
    };

    const handleBold = () => insertMarkdown('**', '**');
    const handleItalic = () => insertMarkdown('_', '_');
    const handleBulletList = () => insertLinePrefix('- ');
    const handleNumberedList = () => insertLinePrefix('1. ');
    const handleLink = () => insertMarkdown('[', '](url)');

    return (
        <div className={"markdown-input-container " + (labelType === null ? "" : labelType === "default" ? "gap-small" : "gap-x-small")}>
            {labelType !== null && (
                <label className={labelStyle + " markdown-input-label"}>
                    {label}
                </label>
            )}
            <div className={"markdown-input-wrapper" + (disabled ? " disabled" : "")}>
                <div className="markdown-input-toolbar">
                    <div className="markdown-input-toolbar-left">
                        <button
                            type="button"
                            className="markdown-input-toolbar-btn"
                            onClick={handleBold}
                            disabled={disabled || previewing}
                            title="Bold"
                        >
                            B
                        </button>
                        <button
                            type="button"
                            className="markdown-input-toolbar-btn markdown-input-toolbar-btn-italic"
                            onClick={handleItalic}
                            disabled={disabled || previewing}
                            title="Italic"
                        >
                            I
                        </button>
                        <div className="markdown-input-toolbar-divider" />
                        <button
                            type="button"
                            className="markdown-input-toolbar-btn"
                            onClick={handleBulletList}
                            disabled={disabled || previewing}
                            title="Bullet List"
                        >
                            &bull;
                        </button>
                        <button
                            type="button"
                            className="markdown-input-toolbar-btn"
                            onClick={handleNumberedList}
                            disabled={disabled || previewing}
                            title="Numbered List"
                        >
                            1.
                        </button>
                        <div className="markdown-input-toolbar-divider" />
                        <button
                            type="button"
                            className="markdown-input-toolbar-btn"
                            onClick={handleLink}
                            disabled={disabled || previewing}
                            title="Link"
                        >
                            &#128279;
                        </button>
                    </div>
                    <button
                        type="button"
                        className={"markdown-input-toolbar-btn markdown-input-toolbar-btn-text" + (previewing ? " markdown-input-toolbar-btn-active" : "")}
                        onClick={() => setPreviewing(!previewing)}
                        disabled={disabled}
                        title={previewing ? "Edit" : "Preview"}
                    >
                        {previewing ? "Edit" : "Preview"}
                    </button>
                </div>
                {previewing ? (
                    value ? (
                        <div
                            className="markdown-input-preview"
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                    ) : (
                        <div className="markdown-input-preview markdown-input-preview-empty">
                            Nothing to preview
                        </div>
                    )
                ) : (
                    <textarea
                        ref={textareaRef}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="markdown-input-textarea"
                        placeholder={placeholder}
                        rows={rows}
                        disabled={disabled}
                    />
                )}
            </div>
        </div>
    );
}

export default MarkdownInput;